import { ArrowRight, Pause, Play } from "@phosphor-icons/react"
import { COVERAGE } from "@tokenterminal/core/data/coverage"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import Head from "next/head"
import { type HTMLAttributes, useCallback, useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { slides } from "@/constants/content"
import Container from "../../common/Container"
import FeaturedContent from "./FeaturedContent"
import HeroHeading from "./HeroHeading"
import HorizontalDottedLine from "./HorizontalDottedLine"
import ImagePane from "./ImagePane"
import RotatingText from "./RotatingText"
import useInterval from "./useTimer"
import VerticalDottedLine from "./VerticalDottedLine"

const GAP = 25
const SLIDE_DURATION = 6000
// Set to a slide index (0-3) to lock to that slide, or null to allow normal rotation
// 0 = understand, 1 = trust, 2 = explore, 3 = act on

const LOCKED_SLIDE_INDEX: number | null = null

type ProgressRingProps = HTMLAttributes<HTMLDivElement> & {
  progress: number
  accentColor: string
  size?: number
  strokeWidth?: number
}

const ProgressRing = ({
  progress,
  accentColor,
  size = 38,
  strokeWidth = 1,
  ...props
}: ProgressRingProps) => {
  const center = size / 2
  const radius = center - strokeWidth / 2
  const circumference = 2 * Math.PI * radius

  return (
    <div {...props}>
      <svg
        width="100%"
        height="100%"
        style={{
          opacity: 0.25,
          transform: "rotate(-90deg)",
        }}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          className="stroke-fg-default/5"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          className="stroke-fg-default"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress / 100)}
          strokeLinecap="round"
        />
      </svg>
    </div>
  )
}

const Hero = () => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(
    LOCKED_SLIDE_INDEX ?? 0
  )
  const currentSlide = slides[currentSlideIndex] || slides[0]
  const [isPlaying, setIsPlaying] = useState(LOCKED_SLIDE_INDEX === null)
  const [isHovering, setIsHovering] = useState(false)
  // The server renders only the first slide. The client adds each slide once it is current or a neighbour
  const [loadedSlides, setLoadedSlides] = useState([currentSlideIndex])

  useEffect(() => {
    const prev = (currentSlideIndex - 1 + slides.length) % slides.length
    const next = (currentSlideIndex + 1) % slides.length
    setLoadedSlides((loaded) => [
      ...new Set([...loaded, prev, currentSlideIndex, next]),
    ])
  }, [currentSlideIndex])

  const nextSlide = useCallback(() => {
    if (LOCKED_SLIDE_INDEX === null) {
      setCurrentSlideIndex((prev) => (prev + 1 + slides.length) % slides.length)
    }
  }, [])

  const { elapsedTime, resetElapsedTime } = useInterval({
    callback: nextSlide,
    duration: SLIDE_DURATION,
    isPlaying,
  })

  useEffect(() => {
    if (LOCKED_SLIDE_INDEX !== null) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        ["INPUT", "TEXTAREA"].includes((event.target as HTMLElement)?.tagName)
      ) {
        return
      }

      if (event.key === "ArrowLeft") {
        resetElapsedTime()
        setCurrentSlideIndex(
          (prev) => (prev - 1 + slides.length) % slides.length
        )
      } else if (event.key === "ArrowRight") {
        resetElapsedTime()
        setCurrentSlideIndex(
          (prev) => (prev + 1 + slides.length) % slides.length
        )
      } else if (event.key === " ") {
        event.preventDefault()
        setIsPlaying((prev) => !prev)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [resetElapsedTime])

  const controlsRow = (
    <div className="flex items-center gap-1">
      <div className="relative">
        <IconButton
          variant="ghost"
          icon={isPlaying || isHovering ? Pause : Play}
          label={isPlaying || isHovering ? "Pause" : "Play"}
          onClick={() => {
            if (LOCKED_SLIDE_INDEX === null) {
              setIsPlaying((prev) => !prev)
            }
          }}
          iconProps={{
            weight: "fill",
          }}
          style={{
            borderRadius: "50%",
            opacity: isHovering ? 1 : 0.25,
            transition: "opacity 200ms",
          }}
        />
        <ProgressRing
          className="pointer-events-none"
          progress={(elapsedTime / SLIDE_DURATION) * 100}
          accentColor={currentSlide.accentColor} // Pass the accentColor of the current slide
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
          }}
        />
      </div>
    </div>
  )

  return (
    <Container
      className={twMerge(
        "flex items-center text-center relative w-[1300px] max-w-[100vw] mx-auto z-2 mb-16",
        "flex flex-col gap-1"
      )}
    >
      <Head>
        {/* The first slide background is the LCP element. CSS alone finds it late */}
        <link
          rel="preload"
          as="image"
          href={slides[LOCKED_SLIDE_INDEX ?? 0].backgroundImage}
          fetchPriority="high"
          key="hero-background"
        />
      </Head>
      <h1 className="sr-only">
        Blockchain data you can understand, trust, explore and act on
      </h1>
      {/* Desktop controls */}
      <div
        className="hidden min-[1460px]:flex flex-col items-start"
        style={{
          position: "absolute",
          right: "-4.5%",
          top: "0",
        }}
      >
        {controlsRow}
      </div>
      {/* Hero grid */}
      <div
        className="hero-grid"
        onMouseEnter={() => {
          if (LOCKED_SLIDE_INDEX === null) {
            setIsHovering(true)
            setIsPlaying(false)
          }
        }}
        onMouseLeave={() => {
          if (LOCKED_SLIDE_INDEX === null) {
            setIsHovering(false)
            setIsPlaying(true)
          }
        }}
      >
        <div
          className={twMerge("flex items-start", "responsive-gap")}
          style={{
            textAlign: "left",
            gridArea: "blockchain",
          }}
        >
          <HeroHeading className="animate-rise-in" animationIndex={0}>
            Blockchain
          </HeroHeading>
          <ImagePane
            currentSlideIndex={currentSlideIndex}
            loadedSlides={loadedSlides}
            containerStyle={{
              flex: 1,
              height: "100%",
              backgroundColor: currentSlide.accentColor,
            }}
            backgroundImageClass="blockchain-image-pane"
          />
        </div>
        <div
          className="flex items-center responsive-gap"
          style={{
            gridArea: "data",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              textAlign: "left",
            }}
          >
            <HeroHeading className="animate-rise-in" animationIndex={1}>
              data
            </HeroHeading>
          </div>
          <div
            style={{
              height: "100%",
              flex: 1,
            }}
          >
            <ImagePane
              currentSlideIndex={currentSlideIndex}
              loadedSlides={loadedSlides}
              containerStyle={{
                height: "100%",
                width: "100%",
                backgroundColor: currentSlide.accentColor,
              }}
              backgroundImageClass="data-image-pane"
            />
          </div>
        </div>
        <div className="hide-section" style={{ gridArea: "section2" }}>
          <ImagePane
            currentSlideIndex={currentSlideIndex}
            loadedSlides={loadedSlides}
            containerStyle={{
              width: "100%",
              height: "100%",
              backgroundColor: currentSlide.accentColor,
              transition: "background-color 1000ms",
            }}
            imageStyle={{
              backgroundPosition: "top right",
            }}
          />
        </div>

        <div className="hide-section" style={{ gridArea: "section4" }}>
          <ImagePane
            currentSlideIndex={currentSlideIndex}
            loadedSlides={loadedSlides}
            containerStyle={{
              width: "100%",
              height: "100%",
              backgroundColor: currentSlide.accentColor,
            }}
            imageStyle={{
              backgroundPosition: "bottom left",
            }}
          />
        </div>
        <div
          className={twMerge("flex items-center", "responsive-gap")}
          style={{
            gridArea: "youcan",
            textAlign: "right",
          }}
        >
          <ImagePane
            currentSlideIndex={currentSlideIndex}
            loadedSlides={loadedSlides}
            containerStyle={{
              height: "100%",
              width: "100%",
              backgroundColor: currentSlide.accentColor,
            }}
            backgroundImageClass="you-can-image-pane"
          />
          <div
            style={{
              textAlign: "right",
            }}
          >
            <HeroHeading className="animate-rise-in" animationIndex={2}>
              you can
            </HeroHeading>
          </div>
        </div>
        <div
          className="flex items-center justify-start gap-2"
          style={{
            gridArea: "changetext",
            textAlign: "right",
            gap: `${GAP / 2}px`,
          }}
        >
          <ImagePane
            currentSlideIndex={currentSlideIndex}
            loadedSlides={loadedSlides}
            containerStyle={{
              flex: 1,
              height: "100%",
              backgroundColor: currentSlide.accentColor,
            }}
            backgroundImageClass="changetext-image-pane"
          />
          <RotatingText currentSlideIndex={currentSlideIndex} />
        </div>

        <div
          className="animate-rise-in"
          style={{
            gridArea: "section3",
          }}
        >
          <FeaturedContent
            currentSlideIndex={currentSlideIndex}
            loadedSlides={loadedSlides}
          />
        </div>

        {/* Lines */}
        <HorizontalDottedLine
          positionStyle={
            {
              top: "-30px",
              gridColumn: "1 / 17",
              gridRow: "blockchain",
              "--gutterWidth": "calc((100vw - var(--hero-width)) / -2)",
              marginLeft: "var(--gutterWidth)",
              marginRight: "var(--gutterWidth)",
            } as React.CSSProperties
          }
        />
        <HorizontalDottedLine
          positionStyle={
            {
              bottom: "10px",
              gridColumn: "1 / 17",
              gridRow: "6",
              "--gutterWidth": "calc((100vw - var(--hero-width)) / -2)",
              marginLeft: "var(--gutterWidth)",
              marginRight: "var(--gutterWidth)",
            } as React.CSSProperties
          }
        />
        <HorizontalDottedLine
          positionStyle={
            {
              gridColumn: "1 / 5",
              gridRow: "3",
              top: "-10px",
              "--gutterWidth": "calc((100vw - var(--hero-width)) / -2)",
              marginLeft: "var(--gutterWidth)",
            } as React.CSSProperties
          }
        />
        <HorizontalDottedLine
          positionStyle={
            {
              gridColumn: "12 / 17",
              gridRow: "4",
              top: "-10px",
              "--gutterWidth": "calc((100vw - var(--hero-width)) / -2)",
              marginRight: "var(--gutterWidth)",
            } as React.CSSProperties
          }
        />
        <VerticalDottedLine
          positionStyle={{
            gridColumn: "1",
            gridRow: "1 / 4",
            left: "-20px",
          }}
        />
        <VerticalDottedLine
          positionStyle={{
            gridColumn: "16",
            gridRow: "1 / 4",
            right: "-75px",
          }}
        />
      </div>
      {/* Footer content */}
      <div
        className={twMerge(
          "flex items-center justify-between py-6 pl-2",
          "hero-content-section"
        )}
        onMouseEnter={() => {
          if (LOCKED_SLIDE_INDEX === null) {
            setIsHovering(true)
            setIsPlaying(false)
          }
        }}
        onMouseLeave={() => {
          if (LOCKED_SLIDE_INDEX === null) {
            setIsHovering(false)
            setIsPlaying(true)
          }
        }}
      >
        <div
          className="text-fg-secondary text-left max-w-[700px] text-base animate-rise-in"
          style={{ animationDelay: "0.2s" }}
        >
          Token Terminal transforms raw blockchain data into institutional-grade
          intelligence. We provide standardized metrics and comparable data
          across {COVERAGE.chains} chains, {COVERAGE.apps} applications, and{" "}
          {COVERAGE.tokenizedAssets} tokenized assets.
        </div>
        <LinkButton
          variant="ghost"
          size="large"
          href={currentSlide.ctaHref}
          className="hidden min-[768px]:inline-flex text-fg-secondary animate-rise-in -mr-3"
          style={{ animationDelay: "0.3s" }}
        >
          {currentSlide.ctaText}
          <Icon as={ArrowRight} />
        </LinkButton>
      </div>
      {/* Mobile controls */}
      <div className="flex items-center justify-between gap-2 w-full">
        <div className="flex min-[1460px]:hidden ml-2">{controlsRow}</div>
        <LinkButton
          variant="ghost"
          size="medium"
          href={currentSlide.ctaHref}
          className="inline-flex min-[768px]:hidden "
          style={{
            marginRight: "-10px",
          }}
        >
          {currentSlide.ctaText === "Learn more about our data pipeline"
            ? "Learn more"
            : currentSlide.ctaText}
          <Icon as={ArrowRight} />
        </LinkButton>
      </div>
    </Container>
  )
}

export default Hero
